import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../../../lib/apiClient.js';
import { formatCurrency, formatDateTime } from '../../../lib/format.js';

const ACTION_LABELS = {
  loan_initiation:        'Loan Initiated',
  hardship_distribution:  'Hardship Distribution',
  in_service_distribution:'In-Service Distribution',
  separation_distribution:'Separation Distribution',
  rmd:                    'Required Minimum Distribution',
  qdro:                   'QDRO Transfer',
  beneficiary_update:     'Beneficiary Update',
  deferral_change:        'Deferral Change',
  investment_reallocation:'Investment Reallocation',
  address_update:         'Address Update',
};

const AUTONOMY_BADGE = {
  full:         { text: 'Executed',      cls: 'bg-success/15 text-success' },
  supervised:   { text: 'Confirmed',     cls: 'bg-accent-light text-accent-dark' },
  human_review: { text: 'Pending Review',cls: 'bg-warning/15 text-warning' },
};

function resolveQueuedBadge(queueStatus) {
  switch (queueStatus) {
    case 'approved':
    case 'approved_awaiting_bank_details':
      return { text: 'Approved', cls: 'bg-success/15 text-success' };
    case 'denied':
      return { text: 'Denied by Admin', cls: 'bg-danger/15 text-danger' };
    case 'cancelled':
      return { text: 'Cancelled', cls: 'bg-bg-s2 text-text-muted' };
    default:
      return { text: 'Pending Review', cls: 'bg-warning/15 text-warning' };
  }
}

function AssetClassBadge({ cls }) {
  const colors = {
    'Target Date':             'bg-blue-500/10 text-blue-600 border-blue-200',
    'US Large Cap Equity':     'bg-emerald-500/10 text-emerald-700 border-emerald-200',
    'US Mid/Small Cap Equity': 'bg-teal-500/10 text-teal-700 border-teal-200',
    'International Equity':    'bg-violet-500/10 text-violet-700 border-violet-200',
    'Fixed Income':            'bg-amber-500/10 text-amber-700 border-amber-200',
    'Stable Value':            'bg-slate-500/10 text-slate-600 border-slate-200',
    'Company Stock':           'bg-orange-500/10 text-orange-700 border-orange-200',
  };
  const c = colors[cls] || 'bg-bg-s3 text-text-faint border-border';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${c}`}>
      {cls}
    </span>
  );
}

function EventRow({ event }) {
  const label = ACTION_LABELS[event.action] || event.action;
  let statusText, statusClass;
  if (event.type === 'fap_decision') {
    if (!event.authorized) {
      statusText  = 'Denied';
      statusClass = 'bg-danger/15 text-danger';
    } else if (event.autonomy_level === 'human_review') {
      const badge = resolveQueuedBadge(event.queue_status);
      statusText  = badge.text;
      statusClass = badge.cls;
    } else {
      const badge = AUTONOMY_BADGE[event.autonomy_level] || { text: 'Executed', cls: 'bg-success/15 text-success' };
      statusText  = badge.text;
      statusClass = badge.cls;
    }
  } else if (event.type === 'loan') {
    statusText  = event.status === 'active' ? 'Loan Active' : (event.status || 'active');
    statusClass = event.status === 'active' ? 'bg-accent-light text-accent-dark' : 'bg-bg-s2 text-text-muted';
  } else {
    statusText  = 'Executed';
    statusClass = 'bg-success/15 text-success';
  }

  return (
    <tr className="border-b border-border last:border-0">
      <td className="px-4 py-3 text-text-muted text-xs tabular-nums whitespace-nowrap">
        {event.timestamp ? formatDateTime(event.timestamp) : '—'}
      </td>
      <td className="px-4 py-3 font-medium text-text">{label}</td>
      <td className="px-4 py-3">
        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${statusClass}`}>
          {statusText}
        </span>
      </td>
      <td className="px-4 py-3 text-right tabular-nums text-sm">
        {event.amount != null ? formatCurrency(event.amount) : '—'}
      </td>
      <td className="px-4 py-3 text-xs text-text-muted max-w-xs truncate">
        {event.type === 'fap_decision' && !event.authorized
          ? (event.denial_code || '—')
          : (event.note || event.erisa_citation || '—')}
      </td>
    </tr>
  );
}

export default function ParticipantActivityPage() {
  const { participantId } = useParams();
  const navigate = useNavigate();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    apiClient.getAdminParticipantActivity(participantId)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [participantId]);

  if (loading) return <p className="p-6 text-text-muted text-sm">Loading…</p>;
  if (error)   return <p className="p-6 text-danger text-sm">Error: {error}</p>;

  const {
    name, employment_status, vested_balance, total_balance,
    deferral_pct, deferral_type, investments = [], events = [],
  } = data;

  const denied   = events.filter(e => e.type === 'fap_decision' && !e.authorized).length;
  const approved = events.filter(e => e.type === 'fap_decision' &&  e.authorized).length;
  const executed = events.filter(e => e.type === 'executed').length;

  const deferralLabel = deferral_pct != null
    ? `${(deferral_pct * 100).toFixed(1).replace(/\.0$/, '')}%`
    : '—';
  const deferralTypeLabel = deferral_type === 'roth' ? 'Roth' : 'Pre-Tax';

  return (
    <div className="max-w-4xl mx-auto space-y-5">

      {/* Back */}
      <button
        onClick={() => navigate('/sponsor/participants')}
        className="text-sm text-accent hover:underline flex items-center gap-1"
      >
        ← All Participants
      </button>

      {/* Summary bar */}
      <div className="card p-4 flex flex-wrap gap-6 items-center">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-widest text-text-faint mb-0.5">Participant</p>
          <p className="font-semibold text-text">{name}</p>
        </div>
        <div>
          <p className="text-[10px] font-mono uppercase tracking-widest text-text-faint mb-0.5">Status</p>
          <p className="font-medium text-text capitalize">{employment_status?.replace(/_/g, ' ')}</p>
        </div>
        <div>
          <p className="text-[10px] font-mono uppercase tracking-widest text-text-faint mb-0.5">Total Balance</p>
          <p className="font-semibold text-text tabular-nums">{formatCurrency(total_balance)}</p>
        </div>
        <div>
          <p className="text-[10px] font-mono uppercase tracking-widest text-text-faint mb-0.5">Vested Balance</p>
          <p className="font-semibold text-success tabular-nums">{formatCurrency(vested_balance)}</p>
        </div>
        <div>
          <p className="text-[10px] font-mono uppercase tracking-widest text-text-faint mb-0.5">Deferral</p>
          <p className="font-medium text-text tabular-nums">{deferralLabel} <span className="text-text-faint text-[11px]">({deferralTypeLabel})</span></p>
        </div>
        <div className="ml-auto flex gap-4 text-center self-center">
          <div><p className="text-lg font-bold text-success">{approved}</p><p className="text-xs text-text-muted">Approved</p></div>
          <div><p className="text-lg font-bold text-danger">{denied}</p><p className="text-xs text-text-muted">Denied</p></div>
          <div><p className="text-lg font-bold text-accent-dark">{executed}</p><p className="text-xs text-text-muted">Executed</p></div>
        </div>
      </div>

      {/* Fund Holdings */}
      <div className="card overflow-hidden">
        <div className="px-5 py-3.5 border-b border-border bg-bg-s2 flex items-center justify-between">
          <h2 className="text-[13px] font-semibold text-text">Fund Holdings</h2>
          <span className="text-[11px] text-text-faint font-mono">{investments.length} fund{investments.length !== 1 ? 's' : ''}</span>
        </div>

        {investments.length === 0 ? (
          <p className="px-5 py-4 text-sm text-text-faint">No investment elections on record.</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="bg-bg-s3 border-b border-border">
                    <th className="text-left font-mono font-normal text-text-faint px-4 py-2.5">Fund</th>
                    <th className="text-left font-mono font-normal text-text-faint px-4 py-2.5">Ticker</th>
                    <th className="text-left font-mono font-normal text-text-faint px-4 py-2.5">Asset Class</th>
                    <th className="text-right font-mono font-normal text-text-faint px-4 py-2.5">Exp. Ratio</th>
                    <th className="text-right font-mono font-normal text-text-faint px-4 py-2.5">Allocation</th>
                    <th className="text-left font-mono font-normal text-text-faint px-4 py-2.5 w-36">Weight</th>
                  </tr>
                </thead>
                <tbody>
                  {investments.map((f) => (
                    <tr key={f.fund_id} className="border-b border-border last:border-0 hover:bg-bg-s2 transition-colors">
                      <td className="px-4 py-2.5 text-text font-medium max-w-[200px]">
                        <span className="truncate block">{f.fund_name}</span>
                        {f.is_qdia && (
                          <span className="text-[9px] font-mono text-text-faint">QDIA</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-text-muted font-mono">{f.ticker || '—'}</td>
                      <td className="px-4 py-2.5"><AssetClassBadge cls={f.asset_class} /></td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-text-muted">
                        {f.expense_ratio != null ? `${(f.expense_ratio * 100).toFixed(2)}%` : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-text">
                        {f.allocation_pct != null ? `${(f.allocation_pct * 100).toFixed(0)}%` : '—'}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="w-28 h-1.5 bg-bg-s3 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-accent rounded-full"
                            style={{ width: `${(f.allocation_pct * 100).toFixed(0)}%` }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-2.5 border-t border-border bg-bg-s2">
              <p className="text-[10px] text-text-faint font-mono">
                Allocations reflect participant's current elections · ERISA §404(c)
              </p>
            </div>
          </>
        )}
      </div>

      {/* Activity table */}
      <div className="card overflow-hidden">
        <div className="px-5 py-3.5 border-b border-border bg-bg-s2">
          <h2 className="text-[13px] font-semibold text-text">Activity History</h2>
        </div>
        {events.length === 0 ? (
          <p className="px-5 py-4 text-sm text-text-faint">No activity recorded yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-bg-s2 border-b border-border text-left text-text-muted font-medium">
                  <th className="px-4 py-3 whitespace-nowrap">Date / Time</th>
                  <th className="px-4 py-3">Action</th>
                  <th className="px-4 py-3">Result</th>
                  <th className="px-4 py-3 text-right">Amount</th>
                  <th className="px-4 py-3">Note / Code</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e, i) => <EventRow key={e.id || i} event={e} />)}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
